import axiosInstance from "../utils/axiosInstance";

export const USAGE_EXPORT_FALLBACK_FILENAME = "usage_export.xlsx";

export function getServiceErrorPayload(err) {
  const data = err?.response?.data;
  const candidates = [data?.error, data?.detail?.error];
  return (
    candidates.find(
      (error) =>
        error &&
        typeof error === "object" &&
        typeof error.code === "string" &&
        error.code.startsWith("SERVICE_")
    ) || null
  );
}

export function getServiceErrorCode(err) {
  return getServiceErrorPayload(err)?.code || null;
}

export function formatApiError(err) {
  const serviceError = getServiceErrorPayload(err);
  if (serviceError?.message) return serviceError.message;

  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(", ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function safeXlsxFilename(value) {
  if (typeof value !== "string") return null;
  const filename = value.trim();
  if (
    !filename ||
    filename === "." ||
    filename === ".." ||
    filename.includes("/") ||
    filename.includes("\\") ||
    /[\u0000-\u001f\u007f]/.test(filename) ||
    !filename.toLowerCase().endsWith(".xlsx")
  ) {
    return null;
  }
  return filename;
}

export function getUsageExportFilename(contentDisposition) {
  if (typeof contentDisposition !== "string") return USAGE_EXPORT_FALLBACK_FILENAME;

  const encodedMatch = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (encodedMatch) {
    try {
      const decoded = decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ""));
      const safeDecoded = safeXlsxFilename(decoded);
      if (safeDecoded) return safeDecoded;
    } catch {
      // Fall through to the regular filename or safe fallback.
    }
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  return (
    safeXlsxFilename(filenameMatch?.[1] || filenameMatch?.[2]) ||
    USAGE_EXPORT_FALLBACK_FILENAME
  );
}

export function buildUsageExportPayload(filters = {}) {
  const payload = {};
  const textFields = [
    "warehouse_id",
    "status",
    "ticket_number",
    "created_by_email",
    "source_type",
    "model",
    "item",
    "serial_number",
    "customer",
  ];
  textFields.forEach((field) => {
    const value = String(filters[field] || "").trim();
    if (value) payload[field] = value;
  });
  if (filters.date_from) payload.date_from = filters.date_from;
  if (filters.date_to) payload.date_to = filters.date_to;
  return payload;
}

async function normalizeBlobApiError(error) {
  const data = error?.response?.data;
  if (!(typeof Blob !== "undefined" && data instanceof Blob)) return error;
  try {
    const text = await data.text();
    error.response = { ...error.response, data: JSON.parse(text) };
  } catch {
    // Preserve the original response when it is not a JSON error.
  }
  return error;
}

export async function exportUsage(payload) {
  try {
    const response = await axiosInstance.post("/usage/export", payload, {
      responseType: "blob",
    });
    return {
      blob: response.data,
      filename: getUsageExportFilename(response.headers?.["content-disposition"]),
    };
  } catch (error) {
    throw await normalizeBlobApiError(error);
  }
}

export function downloadUsageExport(
  { blob, filename },
  { documentRef = document, urlApi = URL } = {}
) {
  const objectUrl = urlApi.createObjectURL(blob);
  let anchor;
  try {
    anchor = documentRef.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename || USAGE_EXPORT_FALLBACK_FILENAME;
    anchor.style.display = "none";
    documentRef.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor?.remove();
    urlApi.revokeObjectURL(objectUrl);
  }
}

export default axiosInstance;
